import { create } from "zustand";
import { supabase } from "@/integrations/supabase/client";
import { useAuthStore } from "@/store/authStore";

interface SubscriptionTier {
  id: string;
  name: string;
  price: number;
  billing_period: string;
  max_stores: number | null;
  max_menus_per_store: number | null;
  has_staff_management: boolean;
  has_chat: boolean;
  has_reservations: boolean;
  has_attendance: boolean;
  has_landing_page: boolean;
  has_priority_support: boolean;
}

interface UserSubscription {
  id: string;
  tier_id: string;
  status: string;
  current_period_end: string | null;
  subscription_tiers: SubscriptionTier;
}

type UpgradeTrigger = {
  type: "store_limit" | "menu_limit" | "feature_access";
  feature?: string;
  currentUsage?: {
    current: number;
    limit: number;
  };
};

interface SubscriptionStore {
  subscription: UserSubscription | null;
  tier: SubscriptionTier | null;
  loading: boolean;
  error: Error | null;
  showUpgradeDialog: boolean;
  currentTrigger: UpgradeTrigger | null;

  // Actions
  checkSubscription: () => Promise<void>;
  createCheckout: (tierId: string, billingPeriod: string) => Promise<unknown>;
  createPaystackSubscription: (planCode: string, email: string) => Promise<unknown>;
  setShowUpgradeDialog: (show: boolean) => void;

  // Computed getters
  canCreateStores: number;
  canAccessFeature: (
    feature: keyof Pick<
      SubscriptionTier,
      | "has_staff_management"
      | "has_chat"
      | "has_reservations"
      | "has_attendance"
      | "has_landing_page"
      | "has_priority_support"
    >
  ) => boolean;

  // Guard checks that handle showing the dialog
  checkStoreLimit: (currentStores: number) => boolean;
  checkMenuLimit: (currentMenuItems: number) => boolean;
  checkFeatureAccess: (
    feature: keyof Pick<
      SubscriptionTier,
      | "has_staff_management"
      | "has_chat"
      | "has_reservations"
      | "has_attendance"
      | "has_landing_page"
      | "has_priority_support"
    >
  ) => boolean;

  // Test function to show upgrade dialog
  testShowUpgradeDialog: () => void;
}

export const useSubscriptionStore = create<SubscriptionStore>((set, get) => ({
  subscription: null,
  tier: null,
  loading: false,
  error: null,
  showUpgradeDialog: false,
  currentTrigger: null,

  setShowUpgradeDialog: (show) => set({ showUpgradeDialog: show }),

  checkSubscription: async () => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase.functions.invoke(
        "check-subscription"
      );
      if (error) throw error;
      set({
        tier: data.tier || null,
        subscription: data.subscription || null,
        loading: false,
      });
    } catch (error) {
      console.error("Error checking subscription:", error);
      set({ error: error as Error, loading: false });
    }
  },

  createCheckout: async (tierId: string, billingPeriod: string) => {
    try {
      const { data, error } = await supabase.functions.invoke(
        "create-checkout",
        {
          body: { tierId, billingPeriod },
        }
      );

      if (error) throw error;

      if (data.url) {
        window.open(data.url, "_blank");
      }

      return data;
    } catch (error) {
      console.error("Error creating checkout:", error);
      throw error;
    }
  },

  createPaystackSubscription: async (planCode: string, email: string) => {
    try {
      const { data: userData, error: userError } =
        await supabase.auth.getUser();
      if (userError || !userData.user) {
        throw new Error("User not authenticated");
      }

      const { data, error } = await supabase.functions.invoke(
        "paystack-subscription",
        {
          body: {
            planCode,
            email,
            userId: userData.user.id,
          },
        }
      );

      if (error) throw error;

      if (data.authorizationUrl) {
        window.open(data.authorizationUrl, "_blank");
      }

      return data;
    } catch (error) {
      console.error("Error creating Paystack subscription:", error);
      throw error;
    }
  },

  get canCreateStores() {
    return get().tier?.max_stores || 0;
  },

  canAccessFeature: (feature) => {
    return get().tier?.[feature] || false;
  },

  // Guard checks that handle showing the dialog
  checkStoreLimit: (currentStores) => {
    const { tier } = get();
    if (!tier?.max_stores) return true; // Unlimited stores

    if (currentStores >= tier.max_stores) {
      set({
        currentTrigger: {
          type: "store_limit",
          currentUsage: {
            current: currentStores,
            limit: tier.max_stores,
          },
        },
        showUpgradeDialog: true,
      });
      return false;
    }
    return true;
  },

  checkMenuLimit: (currentMenuItems) => {
    const { tier } = get();
    if (!tier?.max_menus_per_store) return true; // Unlimited menus

    if (currentMenuItems >= tier.max_menus_per_store) {
      set({
        currentTrigger: {
          type: "menu_limit",
          currentUsage: {
            current: currentMenuItems,
            limit: tier.max_menus_per_store,
          },
        },
        showUpgradeDialog: true,
      });
      return false;
    }
    return true;
  },

  checkFeatureAccess: (feature) => {
    const { tier } = get();
    if (!tier?.[feature]) {
      set({
        currentTrigger: {
          type: "feature_access",
          feature,
        },
        showUpgradeDialog: true,
      });
      return false;
    }
    return true;
  },

  // Test function to show upgrade dialog
  testShowUpgradeDialog: () => {
    // First check if we're in development
    if (process.env.NODE_ENV !== "development") {
      console.warn("Test function only available in development mode");
      return;
    }

    // Set the state
    set({
      showUpgradeDialog: true,
      currentTrigger: {
        type: "store_limit",
        currentUsage: {
          current: 1,
          limit: 1,
        },
      },
    });
  },
}));

// Expose test function globally for debugging
if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).testShowUpgradeDialog = () => {
    useSubscriptionStore.getState().testShowUpgradeDialog();
  };

  // Also expose a function to check all relevant state
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).checkUpgradeDialogState = () => {
    const subStore = useSubscriptionStore.getState();
    const authStore = useAuthStore.getState();

    // Debug info available in development only
    if (process.env.NODE_ENV === "development") {
      console.log("=== Upgrade Dialog Debug Info ===");
      console.log("Auth State:", {
        isLoggedIn: !!authStore.user,
        userRole: authStore.user?.role,
      });
      console.log("Subscription State:", {
        showUpgradeDialog: subStore.showUpgradeDialog,
        currentTrigger: subStore.currentTrigger,
        tier: subStore.tier,
      });
    }
  };
}
